import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { t, eur, fmtDate } from "../../lib/portalI18n";
import { usePortal } from "./PortalShell";

export function Card({ children, className = "", testid }) {
  return <div className={`rounded-2xl bg-white border border-slate-200/80 shadow-[0_1px_2px_rgba(15,28,23,.04)] ${className}`} data-testid={testid}>{children}</div>;
}

export function Disclaimer({ text }) {
  return <div className="text-[11px] leading-snug text-slate-500 bg-slate-100/80 rounded-xl px-3 py-2 mt-3" data-testid="portal-disclaimer">{text}</div>;
}

function Term({ label, value }) {
  return <div><div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div><div className="text-sm font-semibold text-slate-800 tabular-nums">{value ?? "—"}</div></div>;
}

export default function PortalOverview() {
  const { me, lang } = usePortal();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/portal/overview").then((r) => setData(r.data)).catch(() => setData({ contracts: [], clause_count: 0, document_count: 0, approved_total: 0 })); }, []);
  if (!data) return <div className="text-sm text-slate-500">{t(lang, "loading")}</div>;
  return (
    <div className="space-y-5" data-testid="portal-overview">
      <div><div className="text-sm text-slate-500">{t(lang, "hello")} {me.name.split(" ")[0]} 👋</div><h1 className="text-2xl font-semibold tracking-tight">{t(lang, "active_contracts")}</h1></div>
      {data.contracts.length === 0 && <Card className="p-5 text-sm text-slate-500" testid="portal-no-contracts">{t(lang, "no_contracts")}</Card>}
      {data.contracts.map((c) => (
        <Card key={c.id} className="p-5" testid={`portal-contract-${c.id}`}>
          <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold">{(c.doc_type || "").replace("_", " ")}</div>
          <div className="text-lg font-semibold leading-tight mt-0.5">{c.title}</div>
          {c.counterparty && <div className="text-sm text-slate-500">{c.counterparty}</div>}
          <div className="grid grid-cols-2 gap-3 mt-4">
            {"effective_date" in c && <Term label={t(lang, "from")} value={fmtDate(c.effective_date, lang)} />}
            {"expiry_date" in c && <Term label={t(lang, "until")} value={fmtDate(c.expiry_date, lang)} />}
            {"base_wage" in c && <Term label={t(lang, "base_wage")} value={c.base_wage ? `${eur(c.base_wage, c.currency, lang)} ${t(lang, "per_month")}` : "—"} />}
            {"option_to_extend" in c && <Term label={t(lang, "option")} value={c.option_to_extend || "—"} />}
            {"release_clause" in c && <Term label={t(lang, "release")} value={c.release_clause ? eur(c.release_clause, c.currency, lang) : "—"} />}
          </div>
        </Card>
      ))}
      <div className="grid grid-cols-3 gap-3">
        <Link to="/portal/clauses" data-testid="portal-stat-clauses"><Card className="p-4 h-full"><div className="text-2xl font-semibold tabular-nums">{data.clause_count}</div><div className="text-[11px] text-slate-500 leading-tight">{t(lang, "shared_clauses")}</div></Card></Link>
        <Link to="/portal/documents" data-testid="portal-stat-documents"><Card className="p-4 h-full"><div className="text-2xl font-semibold tabular-nums">{data.document_count}</div><div className="text-[11px] text-slate-500 leading-tight">{t(lang, "shared_documents")}</div></Card></Link>
        <Card className="p-4" testid="portal-stat-approved"><div className="text-2xl font-semibold tabular-nums text-emerald-700">{eur(data.approved_total, "EUR", lang)}</div><div className="text-[11px] text-slate-500 leading-tight">{t(lang, "approved_total")}</div></Card>
      </div>
      <Disclaimer text={data.disclaimer || me.disclaimer} />
    </div>
  );
}
